import React, { useState, useMemo } from "react";
import { IProjectResults, ITimeEntry, IUser, IWeekData } from "../../types/types";
import { IDropdownElement } from "../WeekTable";
import QuickFillWeekDialog from "../QuickFillWeekDialog";
import BulkFillHoursDialog from "../BulkFillHoursDialog";
import { DAY_EXPECTED_TOTAL, WEEK_EXPECTED_TOTAL } from "./utils";
import { Add24Regular, ChevronDown24Regular, ChevronUp24Regular, Search24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import WeektableDateCell from "./WeektableDateCell";
import WeekTableProjectNameCell from "./WeekTableProjectNameCell";
import WeektableHideCell from "./WeektableHideCell";
import { Dialog, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Button, Input } from "@fluentui/react-components";
import AddProjectDialog from "../AddProjectDialog";
import * as Constants from "../../utils/constants";
import { getFinnishHoliday } from "../../utils/finnishHolidays";

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

interface IMobileCardViewProps {
    generateRows: () => any[];
    projectsData: IProjectResults[] | undefined;
    weekTotal: IWeekData["weekTotal"];
    selectedDays: number[];
    dayTotal: { [x: string]: number };
    dropdownArr: IDropdownElement[];
    user: IUser;
    jwtoken: string;
    handleProjectOnChange: (project: IDropdownElement) => void;
    handleNewProject: (data: any) => void;
    onQuickFillSuccess: (updatedFieldNames?: string[]) => void;
    field: IWeekData["dates"];
    dbHours: ITimeEntry[];
    setField: (field: IWeekData["dates"]) => void;
    loadingCells: { [key: string]: boolean };
    setLoadingCells: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
    fetchTimeEntries: (week: any[]) => Promise<void>;
    setProjectsData: (projects: IProjectResults[] | undefined) => void;
    focusedCellsRef: React.MutableRefObject<{ [key: string]: boolean }>;
    fieldRef: React.MutableRefObject<IWeekData["dates"] | undefined>;
    changedCells: { [key: string]: boolean };
    localChangesRef: React.MutableRefObject<{ [key: string]: string }>;
    visibleProjects: string[] | undefined;
    setVisibleProjects: (projects: string[] | undefined) => void;
    update: () => void;
    updateUserAppSettings: (jwtoken: string, appSettings: any) => Promise<IUser>;
    bulkFillOnSuccess?: (updatedFieldNames?: string[]) => void;
    showOnlyWithHours: boolean;
    setShowOnlyWithHours: (value: boolean) => void;
    defaultProjectIdByDate: { [date: number]: string | null };
    projectHourTotals: { [projectId: string]: { monthHours: number; yearHours: number } };
    totalsPeriod: { year: number; month: number } | null;
}

export default function MobileCardView(props: IMobileCardViewProps) {
    const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
    const [openProjectDialog, setOpenProjectDialog] = useState<number | null>(null);
    const [projectSearchQuery, setProjectSearchQuery] = useState("");
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    const toggleDay = (dayIndex: number) => {
        setExpandedDays((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(dayIndex)) {
                newSet.delete(dayIndex);
            } else {
                newSet.add(dayIndex);
            }
            return newSet;
        });
    };

    // Group projects by company for the dialog
    const groupedProjects = useMemo(() => {
        const groups: { [key: string]: IDropdownElement[] } = {};

        props.dropdownArr.forEach((project) => {
            const company = project.label || "Other";
            if (!groups[company]) {
                groups[company] = [];
            }
            groups[company].push(project);
        });

        // Sort projects within each group
        Object.keys(groups).forEach((key) => {
            groups[key].sort((a, b) => a.value.localeCompare(b.value));
        });

        return groups;
    }, [props.dropdownArr]);

    // Filter projects based on search
    const filteredGroups = useMemo(() => {
        if (!projectSearchQuery.trim()) {
            return groupedProjects;
        }

        const query = projectSearchQuery.toLowerCase();
        const filtered: { [key: string]: IDropdownElement[] } = {};

        Object.keys(groupedProjects).forEach((company) => {
            const matchingProjects = groupedProjects[company].filter(
                (project) => project.value.toLowerCase().includes(query) || company.toLowerCase().includes(query),
            );
            if (matchingProjects.length > 0) {
                filtered[company] = matchingProjects;
            }
        });

        return filtered;
    }, [groupedProjects, projectSearchQuery]);

    const handleProjectSelect = (project: IDropdownElement, dayIndex: number) => {
        props.handleProjectOnChange(project);
        setOpenProjectDialog(null);
        setProjectSearchQuery("");
    };

    const handleCreateNew = () => {
        setOpenProjectDialog(null);
        setProjectSearchQuery("");
        setShowCreateDialog(true);
    };

    const handleCreateComplete = () => {
        setShowCreateDialog(false);
    };

    // Get projects for a specific day
    const getProjectsForDay = useMemo(() => {
        const getProjects = (dayIndex: number) => {
            const day = props.selectedDays[dayIndex];
            if (!day || !props.projectsData) return [];

            const dayTimestamp = day.valueOf();
            const projectsForDay: Array<{
                project: IProjectResults;
                hours: string;
                fieldName: string;
                isDefaultForDay: boolean;
                isAdminDefaultInWeek: boolean;
            }> = [];

            props.projectsData.forEach((project) => {
                const projectID = project.id!;
                const fieldName = `${projectID}_${dayTimestamp}`;
                const hours = props.field[fieldName] || "";

                // Check if project has hours for this day
                const hasHours = hours && hours !== "" && hours !== "0";

                // Check if project is pinned/visible
                const isPinned = props.visibleProjects?.includes(projectID);

                // Check if it's the base default project
                const isDefaultProject = projectID === Constants.DEFAULT_PROJECT_ID;

                // Check if this project is the admin-selected default project for at least one weekday in the current week
                const isAdminDefaultInWeek = props.selectedDays.slice(1, 8).some((weekDay) => {
                    const dayOfWeek = new Date(weekDay).getDay(); // 0 = Sunday, 6 = Saturday
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    if (isWeekend) return false;
                    const configured = props.defaultProjectIdByDate[weekDay];
                    const effectiveDefaultProjectId = configured ?? Constants.DEFAULT_PROJECT_ID;
                    return effectiveDefaultProjectId === projectID && projectID !== Constants.DEFAULT_PROJECT_ID;
                });

                // Include project if it has hours, is pinned, is base default project, or is admin default for the week
                if (hasHours || isPinned || isDefaultProject || isAdminDefaultInWeek) {
                    // Check if we should filter by hours
                    const weekTotalValue = props.weekTotal[projectID] || 0;
                    const hasWeekHours = weekTotalValue > 0;

                    // If filter is enabled, only show projects with hours (or default project)
                    if (props.showOnlyWithHours && !hasWeekHours && !isDefaultProject) {
                        return; // Skip this project
                    }

                    const configuredDefaultProjectId = props.defaultProjectIdByDate[dayTimestamp];
                    const effectiveDefaultProjectId = configuredDefaultProjectId ?? Constants.DEFAULT_PROJECT_ID;
                    const dayOfWeek = new Date(dayTimestamp).getDay(); // 0 = Sunday, 6 = Saturday
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const isDefaultForDay = !isWeekend && projectID === effectiveDefaultProjectId;

                    projectsForDay.push({
                        project,
                        hours: hours || "",
                        fieldName,
                        isDefaultForDay,
                        isAdminDefaultInWeek,
                    });
                }
            });

            // Sort projects: default project first, then admin-selected default project for the week,
            // then by project name so order matches desktop view behavior.
            projectsForDay.sort((a, b) => {
                // Base default project always first
                if (a.project.id === Constants.DEFAULT_PROJECT_ID) return -1;
                if (b.project.id === Constants.DEFAULT_PROJECT_ID) return 1;

                // Admin-selected default project for the week next
                if (a.isAdminDefaultInWeek && !b.isAdminDefaultInWeek) return -1;
                if (!a.isAdminDefaultInWeek && b.isAdminDefaultInWeek) return 1;

                return a.project.project_name.localeCompare(b.project.project_name);
            });

            return projectsForDay;
        };
        return getProjects;
    }, [
        props.selectedDays,
        props.projectsData,
        props.field,
        props.visibleProjects,
        props.weekTotal,
        props.showOnlyWithHours,
        props.defaultProjectIdByDate,
    ]);

    return (
        <>
            <div className="w-fullmin-h-screen text-(--colorNeutralForeground1)">
                {/* No extra controls here; week options are available via the settings button next to TODAY in the header */}

                {/* Week View - Day Cards */}
                <div className="px-0 py-2 space-y-1">
                    {props.selectedDays.slice(1, 8).map((day, dayIndex) => {
                        const dateObj = new Date(day);
                        const weekdayName = dateObj.toLocaleDateString("en-FI", { weekday: "long" });
                        const dateString = dateObj.toLocaleDateString("en-FI", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                        });
                        const today = new Date();
                        const isToday = dateObj.toDateString() === today.toDateString();
                        const holiday = getFinnishHoliday(dateObj);
                        const dayTotalValue = props.dayTotal[day.toString()] ?? 0;
                        const dayTotalColor =
                            dayTotalValue >= DAY_EXPECTED_TOTAL ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400";
                        const isExpanded = expandedDays.has(dayIndex);
                        // dayIndex in map is 0-6, but selectedDays.slice(1,8) gives indices 1-7, so we use dayIndex + 1
                        const projectsForDay = getProjectsForDay(dayIndex + 1);
                        const isDialogOpen = openProjectDialog === dayIndex;

                        return (
                            <div
                                key={dayIndex}
                                className={`rounded-xl border shadow-sm overflow-hidden ${
                                    isToday
                                        ? "border-blue-300 dark:border-blue-600 shadow-md bg-(--colorNeutralBackground1)"
                                        : holiday
                                          ? "border-(--colorNeutralStroke2) bg-(--colorNeutralBackground2)"
                                          : "border-(--colorNeutralStroke2) bg-(--colorNeutralBackground2)"
                                }`}
                                title={holiday ? holiday.name : undefined}
                            >
                                {/* Day Header - Clickable */}
                                <button
                                    onClick={() => toggleDay(dayIndex)}
                                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-(--colorNeutralBackground2) transition-colors text-left ${
                                        isToday ? "bg-blue-50/30 dark:bg-blue-950/20" : ""
                                    }`}
                                    {...(isExpanded ? { "aria-expanded": "true" } : { "aria-expanded": "false" })}
                                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${weekdayName}`}
                                >
                                    <div className="flex-1 min-w-0 flex items-center gap-3">
                                        <div className="shrink-0 text-(--colorNeutralForeground2)">
                                            {isExpanded ? (
                                                <ChevronUp24Regular className="w-5 h-5" />
                                            ) : (
                                                <ChevronDown24Regular className="w-5 h-5" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div
                                                className={`text-base font-semibold ${isToday ? "text-blue-600 dark:text-blue-400" : "text-(--colorNeutralForeground1)"}`}
                                            >
                                                {weekdayName}
                                            </div>
                                            <div
                                                className={`text-sm ${isToday ? "text-blue-600 dark:text-blue-400" : "text-(--colorNeutralForeground2)"}`}
                                            >
                                                {dateString}
                                            </div>
                                            {holiday && (
                                                <div className="mt-0.5 text-xs font-semibold text-(--colorNeutralForeground2)">
                                                    {holiday.name}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 ml-2">
                                        <div className="text-right">
                                            <div className="text-[0.65rem] text-(--colorNeutralForeground2) font-medium">Total</div>
                                            <div className={`text-lg font-bold ${dayTotalColor}`}>{dayTotalValue.toFixed(1)}</div>
                                        </div>
                                    </div>
                                </button>

                                {/* Projects List - Collapsible */}
                                {isExpanded && (
                                    <div className="px-4 py-3 border-t border-(--colorNeutralStroke2)">
                                        {/* Projects */}
                                        <div className="space-y-2 mb-3">
                                            {projectsForDay.length === 0 ? (
                                                <div className="px-3 py-4 text-center bg-(--colorNeutralBackground2) rounded-lg border border-gray-200 dark:border-gray-600">
                                                    <p className="text-sm font-medium text-(--colorNeutralForeground2)">
                                                        No projects for this day
                                                    </p>
                                                </div>
                                            ) : (
                                                projectsForDay.map(({ project, hours, fieldName, isDefaultForDay }) => {
                                                    const projectID = project.id!;
                                                    const isProjectLocked = project.entry_locked || false;
                                                    const activeFrom = project.active.from;
                                                    const activeTo = project.active.to;
                                                    let isLocked = isProjectLocked;

                                                    // Check if this specific day is locked due to active date range
                                                    if (!isLocked) {
                                                        if (activeFrom && !(activeFrom <= day.valueOf())) {
                                                            isLocked = true;
                                                        }
                                                        if (activeTo && !(day.valueOf() <= activeTo)) {
                                                            isLocked = true;
                                                        }
                                                    }

                                                    const isPrivate = project.private;
                                                    const textColor = isPrivate
                                                        ? "text-green-600"
                                                        : projectID === Constants.DEFAULT_PROJECT_ID
                                                          ? "text-red-600"
                                                          : "text-orange-500";

                                                    const privText = isPrivate
                                                        ? "Private Project"
                                                        : projectID === Constants.DEFAULT_PROJECT_ID
                                                          ? "Default Project (Always visible)"
                                                          : "Public Project";

                                                    const isDefaultInWeek = props.selectedDays.slice(1, 8).some((weekDay) => {
                                                        const dayOfWeek = new Date(weekDay).getDay(); // 0 = Sunday, 6 = Saturday
                                                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                                                        if (isWeekend) return false;
                                                        const configured = props.defaultProjectIdByDate[weekDay];
                                                        const effectiveDefaultProjectId = configured ?? Constants.DEFAULT_PROJECT_ID;
                                                        return effectiveDefaultProjectId === projectID;
                                                    });

                                                    return (
                                                        <div
                                                            key={projectID}
                                                            className={`rounded-lg border p-3 ${
                                                                isLocked && projectID !== Constants.DEFAULT_PROJECT_ID
                                                                    ? "border-red-200 dark:border-red-900/50 bg-red-50/40 dark:bg-red-950/20"
                                                                    : "border-(--colorNeutralStroke2) bg-(--colorNeutralBackground1)"
                                                            } shadow-[0_1px_3px_rgba(0,0,0,0.18)]`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                {/* Project Info + Hide toggle */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="min-w-0">
                                                                            <WeekTableProjectNameCell
                                                                                projectName={project.project_name}
                                                                                companyName={project.company_name}
                                                                                textColor={textColor}
                                                                                privText={privText}
                                                                                projectId={projectID}
                                                                                isLocked={isLocked}
                                                                                isDefaultInWeek={isDefaultInWeek}
                                                                                note={project.note}
                                                                                monthHours={
                                                                                    props.totalsPeriod
                                                                                        ? (props.projectHourTotals[projectID]?.monthHours ?? 0)
                                                                                        : undefined
                                                                                }
                                                                                yearHours={
                                                                                    props.totalsPeriod
                                                                                        ? (props.projectHourTotals[projectID]?.yearHours ?? 0)
                                                                                        : undefined
                                                                                }
                                                                                monthLabel={
                                                                                    props.totalsPeriod
                                                                                        ? `${MONTH_NAMES[props.totalsPeriod.month - 1]} ${props.totalsPeriod.year}`
                                                                                        : undefined
                                                                                }
                                                                                yearLabel={
                                                                                    props.totalsPeriod ? String(props.totalsPeriod.year) : undefined
                                                                                }
                                                                            />
                                                                        </div>
                                                                        <div className="shrink-0 pl-1">
                                                                            <WeektableHideCell
                                                                                projectId={projectID}
                                                                                disHasHours={(props.weekTotal[projectID] || 0) > 0}
                                                                                projectName={project.project_name}
                                                                                jwtoken={props.jwtoken}
                                                                                user={props.user}
                                                                                visibleProjects={props.visibleProjects}
                                                                                setVisibleProjects={props.setVisibleProjects}
                                                                                update={props.update}
                                                                                updateUserAppSettings={props.updateUserAppSettings}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {/* Hours Input (aligned as last column) */}
                                                                <div className="shrink-0 w-24">
                                                                    <WeektableDateCell
                                                                        field={props.field}
                                                                        projectID={projectID}
                                                                        fieldName={fieldName}
                                                                        dbHours={props.dbHours}
                                                                        date={day}
                                                                        locked={isLocked}
                                                                        jwtoken={props.jwtoken}
                                                                        loadingCells={props.loadingCells}
                                                                        placeholder={
                                                                            isLocked && projectID !== Constants.DEFAULT_PROJECT_ID
                                                                                ? "Locked"
                                                                                : ""
                                                                        }
                                                                        project={project}
                                                                        selectedDays={props.selectedDays}
                                                                        user={props.user}
                                                                        setField={props.setField}
                                                                        setLoadingCells={props.setLoadingCells}
                                                                        fetchTimeEntries={props.fetchTimeEntries}
                                                                        setProjectsData={props.setProjectsData}
                                                                        projectsData={props.projectsData}
                                                                        focusedCellsRef={props.focusedCellsRef}
                                                                        fieldRef={props.fieldRef}
                                                                        changedCells={props.changedCells}
                                                                        localChangesRef={props.localChangesRef}
                                                                        isDefaultForDay={isDefaultForDay}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>

                                        {/* Add Project Button */}
                                        {props.dropdownArr && props.dropdownArr.length > 0 && (
                                            <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                                                <Dialog
                                                    open={isDialogOpen}
                                                    onOpenChange={(_, data) => {
                                                        setOpenProjectDialog(data.open ? dayIndex : null);
                                                        if (!data.open) {
                                                            setProjectSearchQuery("");
                                                        }
                                                    }}
                                                >
                                                    <DialogTrigger disableButtonEnhancement>
                                                        <button
                                                            onClick={() => setOpenProjectDialog(dayIndex)}
                                                            className="w-full px-4 py-2.5 flex items-center justify-center gap-2 rounded-lg border border-(--colorNeutralStroke2) bg-(--colorNeutralBackground1) hover:bg-(--colorNeutralBackground1Hover) transition-colors text-sm font-semibold text-(--colorNeutralForeground1) shadow-[0_1px_3px_rgba(0,0,0,0.18)]"
                                                            aria-label="Add project to this day"
                                                        >
                                                            <Add24Regular className="w-5 h-5" />
                                                            <span>Add Project</span>
                                                        </button>
                                                    </DialogTrigger>
                                                    <DialogSurface className="w-full max-w-[100vw] h-screen sm:h-[90vh] sm:max-h-[85vh] sm:max-w-[90vw] md:max-w-[700px] flex flex-col m-0 sm:m-auto">
                                                        <DialogBody className="flex flex-col h-full overflow-hidden p-0">
                                                            <div className="sticky top-0 z-10 bg-(--colorNeutralBackground1) border-b border-gray-200 dark:border-gray-600">
                                                                <div className="px-4 py-3 sm:px-5 sm:py-4">
                                                                    <div className="flex items-center justify-between mb-3 gap-3">
                                                                        <DialogTitle className="text-base sm:text-lg font-semibold text-(--colorNeutralForeground1) flex-1 min-w-0">
                                                                            Select Project
                                                                        </DialogTitle>
                                                                        <DialogTrigger disableButtonEnhancement>
                                                                            <Button
                                                                                appearance="subtle"
                                                                                icon={<Dismiss24Regular />}
                                                                                onClick={() => {
                                                                                    setOpenProjectDialog(null);
                                                                                    setProjectSearchQuery("");
                                                                                }}
                                                                                className="min-h-[32px] min-w-[32px] p-0 shrink-0"
                                                                                aria-label="Close dialog"
                                                                            />
                                                                        </DialogTrigger>
                                                                    </div>
                                                                    <div className="flex gap-2 sm:gap-3">
                                                                        <div className="relative flex-1 min-w-0">
                                                                            <Search24Regular className="absolute left-2.5 top-1/2 -translate-y-1/2 text-(--colorNeutralForeground3) w-4 h-4 pointer-events-none" />
                                                                            <Input
                                                                                placeholder="Search projects..."
                                                                                value={projectSearchQuery}
                                                                                onChange={(e) => {
                                                                                    const target = e.target as HTMLInputElement;
                                                                                    setProjectSearchQuery(target.value);
                                                                                }}
                                                                                className="w-full pl-9 pr-3 h-10 text-sm"
                                                                                autoFocus
                                                                            />
                                                                        </div>
                                                                        <Button
                                                                            appearance="primary"
                                                                            className="min-h-[40px] px-4 sm:px-5 touch-manipulation text-sm font-medium shrink-0"
                                                                            icon={<Add24Regular />}
                                                                            onClick={handleCreateNew}
                                                                        >
                                                                            <span className="hidden sm:inline">Create New Project</span>
                                                                            <span className="sm:hidden">Create</span>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <DialogContent className="flex-1 overflow-y-auto p-0">
                                                                {Object.keys(filteredGroups).length === 0 ? (
                                                                    <div className="px-4 py-6 text-center">
                                                                        <p className="text-sm text-(--colorNeutralForeground2)">
                                                                            {projectSearchQuery
                                                                                ? "No projects found matching your search"
                                                                                : "No projects available"}
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    <div className="divide-y divide-gray-200 dark:divide-gray-600">
                                                                        {Object.keys(filteredGroups)
                                                                            .sort()
                                                                            .map((company) => (
                                                                                <div key={company} className="py-0.5">
                                                                                    <div className="px-4 py-1.5 sm:px-5 text-[0.7rem] font-semibold text-(--colorNeutralForeground2) uppercase tracking-wide bg-(--colorNeutralBackground2) sticky top-0 z-5">
                                                                                        {company}
                                                                                    </div>
                                                                                    <div>
                                                                                        {filteredGroups[company].map((project) => {
                                                                                            const stableKey =
                                                                                                project.project_id ||
                                                                                                project.key ||
                                                                                                `${company}-${String(project.value)}`;

                                                                                            return (
                                                                                                <button
                                                                                                    key={stableKey}
                                                                                                    onClick={() =>
                                                                                                        handleProjectSelect(
                                                                                                            project,
                                                                                                            dayIndex,
                                                                                                        )
                                                                                                    }
                                                                                                    className="w-full px-4 py-2.5 sm:px-5 text-left hover:bg-(--colorNeutralBackground1Hover) active:bg-(--colorNeutralBackground1Pressed) transition-colors touch-manipulation min-h-[48px] flex items-center"
                                                                                                >
                                                                                                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                                                                        <div
                                                                                                            className={`text-sm font-medium leading-tight ${project.private ? "text-green-600 dark:text-green-400" : "text-orange-500"}`}
                                                                                                        >
                                                                                                            {project.value}
                                                                                                        </div>
                                                                                                        {project.label && (
                                                                                                            <div className="text-xs text-(--colorNeutralForeground2) mt-0.5 leading-tight truncate">
                                                                                                                {project.label}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </button>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                )}
                                                            </DialogContent>

                                                            {/* <div className="hidden sm:block sticky bottom-0 border-t border-gray-200 dark:border-gray-600 bg-(--colorNeutralBackground1) px-5 py-3 shadow-lg">
                                                            <Button
                                                                appearance="primary"
                                                                className="w-full min-h-[44px] touch-manipulation text-sm font-medium"
                                                                icon={<Add24Regular />}
                                                                onClick={handleCreateNew}
                                                            >
                                                                Create New Project
                                                            </Button>
                                                        </div> */}
                                                        </DialogBody>
                                                    </DialogSurface>
                                                </Dialog>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Week Total Summary */}
                <div className="px-0 py-0">
                    <div className="mx-1 bg-(--colorNeutralBackground1) rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm px-4 py-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-(--colorNeutralForeground1) uppercase tracking-wide">
                                Week Total
                            </span>
                            <span
                                className={`text-lg font-bold ${
                                    Object.values(props.weekTotal).reduce((acc, curr) => acc + curr, 0) >= WEEK_EXPECTED_TOTAL
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-red-600 dark:text-red-400"
                                }`}
                            >
                                {Object.values(props.weekTotal)
                                    .reduce((acc, curr) => acc + curr, 0)
                                    .toFixed(1)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Create Project Dialog - Only render when needed (mobile view) */}
            {showCreateDialog && (
                <AddProjectDialog
                    dropdownArr={props.dropdownArr}
                    user={props.user}
                    token={props.jwtoken}
                    initialOpen={true}
                    onOpenChange={(open) => {
                        if (!open) {
                            handleCreateComplete();
                        }
                    }}
                    handleSelectedProject={(project) => {
                        props.handleProjectOnChange(project);
                        handleCreateComplete();
                    }}
                    passChildData={(data) => {
                        props.handleNewProject(data);
                        handleCreateComplete();
                    }}
                />
            )}
        </>
    );
}
